import { useTranslation } from '@pypestream/translations';
import {
  AccessSetupScreen,
  type AccessSetupScreenLabels,
} from '@pypestream/ui-patterns';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { urqlGql } from '@pypestream/api-services/urql';

import { useAccessSetup } from '../../hooks/use-access-setup';
import { resolveAccessReturnUrl, withAccessGrantedFlag } from '../../utils';
import { useManagerCtxSelector } from '../../xstate/app.xstate';
import { IPage } from '../types';

/**
 * Performs the role assignment. urql resolves with `{ data, error }` and never
 * throws, so a returned `error` (a `CombinedError` carrying the GraphQL error
 * codes) is turned into a throw here — `useAccessSetup` needs a rejection to
 * classify a denial from a transient failure.
 */
const assignProjectRoles = async (projectId: string): Promise<void> => {
  const { error } = await urqlGql.assignProjectProductAdminRoles({ projectId });
  if (error) {
    throw error;
  }
};

/**
 * The screen's text in Manager's own translation scope. The screen itself lives
 * in `@pypestream/ui-patterns`, which holds no text — see
 * `AccessSetupScreenLabels`.
 */
const useAccessSetupLabels = (): AccessSetupScreenLabels => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      loading: {
        body: String(t('manager/common:accessSetup.loading.body')),
        note: String(t('manager/common:accessSetup.loading.note')),
      },
      failed: {
        headline: String(t('manager/common:accessSetup.failed.headline')),
        body: String(t('manager/common:accessSetup.failed.body')),
      },
      denied: {
        headline: String(t('manager/common:accessSetup.denied.headline')),
        body: String(t('manager/common:accessSetup.denied.body')),
      },
      actions: {
        myAccount: String(t('manager/common:accessSetup.actions.myAccount')),
        retry: String(t('manager/common:accessSetup.actions.retry')),
        home: String(t('manager/common:accessSetup.actions.home')),
      },
    }),
    [t]
  );
};

interface AccessSetupRunnerProps {
  projectId: string;
  returnUrl: string | null;
  home: string;
  goToMyAccount: () => void;
  goHome: () => void;
  labels: AccessSetupScreenLabels;
}

/**
 * The live flow: call the assignment mutation, wait the fixed Kafka-sync window,
 * then redirect. Split from the page so the mutation only fires when there is a
 * project to set up — the no-project case renders the denied view without it.
 */
const AccessSetupRunner: FC<AccessSetupRunnerProps> = ({
  projectId,
  returnUrl,
  home,
  goToMyAccount,
  goHome,
  labels,
}) => {
  const navigate = useNavigate();

  const onGranted = useCallback(
    (url: string | null) => {
      // Carry the access-granted flag to the destination so it can show the
      // persistent "Access granted" toast after the navigation (PSTUDIO-4098).
      const target = withAccessGrantedFlag(url ?? home, window.location.origin);
      // `resolveAccessReturnUrl` lets an absolute URL on another Pypestream
      // subdomain through (the original link often points at Pro Studio), which
      // React Router cannot navigate to — hand those to the browser.
      if (/^https?:\/\//.test(target)) {
        window.location.assign(target);
      } else {
        navigate(target, { replace: true });
      }
    },
    [home, navigate]
  );

  const { status, retry } = useAccessSetup({
    projectId,
    returnUrl,
    run: assignProjectRoles,
    onGranted,
  });

  // `granted` is the brief window between the wait finishing and the redirect
  // committing; keep the loader up so the screen does not flash.
  if (status === 'loading' || status === 'granted') {
    return (
      <AccessSetupScreen
        status="loading"
        labels={labels}
        onMyAccount={goToMyAccount}
      />
    );
  }

  if (status === 'failed') {
    return (
      <AccessSetupScreen
        status="failed"
        labels={labels}
        onMyAccount={goToMyAccount}
        onRetry={retry}
      />
    );
  }

  return (
    <AccessSetupScreen
      status="denied"
      labels={labels}
      onMyAccount={goToMyAccount}
      onHome={goHome}
    />
  );
};

/**
 * The unified unauthorized-access loader (PSTUDIO-4098). A user without a role
 * on a project is redirected here with `?projectId=<id>&returnUrl=<url>`; the
 * page assigns the product-admin roles for that project, waits for the role to
 * propagate through Kafka, and returns the user to where they were headed. When
 * the assignment is refused — or no project was named — it shows the no-access
 * view instead.
 */
export const AccessSetupPage: FC<IPage> = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Use the canonical routes from context, not a hardcoded path — same source
  // the user menu's "My Account" item navigates to (primary-nav.tsx).
  const { home, myAccount } = useManagerCtxSelector((ctx) => ctx.routes);
  const labels = useAccessSetupLabels();

  const projectId = searchParams.get('projectId') ?? '';
  const returnUrl = resolveAccessReturnUrl(
    searchParams.get('returnUrl'),
    window.location.origin
  );

  const goToMyAccount = useCallback(() => {
    navigate(myAccount);
  }, [navigate, myAccount]);

  const goHome = useCallback(() => {
    navigate(home);
  }, [navigate, home]);

  // No project to set up access for: nothing to assign, so go straight to the
  // no-access view rather than firing a mutation that can only fail.
  if (!projectId) {
    return (
      <AccessSetupScreen
        status="denied"
        labels={labels}
        onMyAccount={goToMyAccount}
        onHome={goHome}
      />
    );
  }

  return (
    <AccessSetupRunner
      projectId={projectId}
      returnUrl={returnUrl}
      home={home}
      goToMyAccount={goToMyAccount}
      goHome={goHome}
      labels={labels}
    />
  );
};
